import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categoryNames, movies } from "@/mocks/movies";
import { formatRating, getMovieById } from "@/utils/movies";
import { useSEO } from "@/hooks/useSEO";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import Recommendations from "@/pages/detail/components/Recommendations";

export default function DetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const movie = getMovieById(movies, id);
  const [copied, setCopied] = useState(false);
  const { toggle, isFavorite } = useFavorites();

  useSEO(
    movie ? `${movie.title} - 色虎视频` : "影视详情 - 色虎视频",
    movie ? movie.synopsis.slice(0, 110) : "色虎视频影视资源详情页。",
  );

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-film-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该影视资源</h1>
        <p className="mt-2 text-sm text-foreground-500">链接可能已失效，或该内容已下架。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50"
        >
          <i className="ri-home-4-line" aria-hidden="true" />
          返回首页
        </Link>
      </div>
    );
  }

  const categoryName = categoryNames[movie.category] ?? "影视";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = `${movie.title} - 色虎视频`;
  const favorited = isFavorite(movie.id);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl || `${movie.title}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const metaRows: { label: string; value: string }[] = [
    { label: "上映年份", value: String(movie.year) },
    { label: "地区", value: movie.region },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join("、") },
    { label: "片长集数", value: movie.duration },
    { label: "画质", value: movie.quality },
    { label: "上架日期", value: movie.updatedAt },
  ];

  return (
    <div className="pb-4">
      <section className="relative w-full">
        <div className="absolute inset-0">
          <img
            src={movie.banner || movie.cover}
            alt={`${movie.title} 背景海报`}
            title={`${movie.title} 高清影视`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/80 to-foreground-950/60" />
        </div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-background-100/80">
            <li className="flex items-center gap-1.5">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </li>
            <li className="flex items-center gap-1.5">
              <Link to={`/category/${movie.category}`} className="transition-colors hover:text-accent-300">
                {categoryName}
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </li>
            <li className="text-background-50">{movie.title}</li>
          </ol>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end">
            <div className="hidden h-64 w-44 shrink-0 overflow-hidden rounded-lg border border-background-50/20 md:block">
              <img
                src={movie.cover}
                alt={movie.title}
                title={`${movie.title} 海报`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-background-100">
                <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 font-label font-bold text-background-50">
                  <i className="ri-star-fill text-xs" aria-hidden="true" />
                  {formatRating(movie.rating)}
                </span>
                <span>{movie.year}</span>
                <span aria-hidden="true">·</span>
                <span>{movie.region}</span>
                <span aria-hidden="true">·</span>
                <span>{movie.genres.join(" / ")}</span>
                <span aria-hidden="true">·</span>
                <span>{movie.duration}</span>
                <span className="rounded-md border border-background-50/30 px-2 py-0.5 text-xs">
                  {movie.quality}
                </span>
              </div>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-100/90 md:text-base">
                {movie.synopsis}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggle(movie.id)}
                  className={`inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 text-sm font-semibold transition-colors ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-background-50 text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"} aria-hidden="true" />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className={copied ? "ri-check-line text-lg" : "ri-link text-lg"} aria-hidden="true" />
                  {copied ? "链接已复制" : "复制链接"}
                </button>
                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-arrow-left-line text-lg" aria-hidden="true" />
                  返回{categoryName}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto mt-8 grid w-full max-w-[1280px] grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-3">
        <div className="flex flex-col gap-8 lg:col-span-2">
          <section>
            <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
              <img
                src={movie.cover}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-25"
              />
              <div className="relative flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-play-fill text-3xl" aria-hidden="true" />
                </span>
                <p className="mt-4 text-sm font-medium text-background-50">在线播放区域（演示占位）</p>
                <p className="mt-1 max-w-sm px-4 text-xs text-background-100/80">
                  本站为影视资源聚合演示平台，暂不提供真实播放服务，此处仅作功能占位展示。
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">剧情简介</h2>
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{movie.synopsis}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                《{movie.title}》由{movie.director}执导，{movie.actors.join("、")}等主演，是一部
                {movie.region}出品的{movie.genres.join("、")}作品，于{movie.year}年推出。
              </p>
            </div>
          </section>

          <Recommendations movie={movie} all={movies} />
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">详细信息</h2>
            <dl className="mt-3 flex flex-col divide-y divide-background-200">
              {metaRows.map((row) => (
                <div key={row.label} className="flex gap-4 py-2.5 text-sm">
                  <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">分享到</h2>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <a
                href={`https://elitetree.cn/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-200 py-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-weibo-fill text-xl" aria-hidden="true" />
                微博
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-200 py-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-twitter-x-fill text-xl" aria-hidden="true" />
                X
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-200 py-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className={copied ? "ri-check-line text-xl" : "ri-file-copy-line text-xl"} aria-hidden="true" />
                {copied ? "已复制" : "复制"}
              </button>
            </div>
          </section>

          <section className="rounded-lg bg-primary-700 p-5">
            <h2 className="font-heading text-base font-bold text-background-50">继续浏览</h2>
            <p className="mt-2 text-sm text-background-100/90">
              想看更多{movie.genres[0]}题材的{movie.quality}作品？返回{movie.region}影视列表继续发现。
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to={`/category/${movie.category}`}
                className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-arrow-left-line" aria-hidden="true" />
                返回{categoryName}
              </Link>
              <Link
                to="/"
                className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-home-4-line" aria-hidden="true" />
                回到首页
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}